import { FC, ReactNode, useEffect, useState } from 'react';

type AssetCache = Record<string, Promise<HTMLElement>>;
let JS_ASSET_CACHE: AssetCache = {};
let CSS_ASSET_CACHE: AssetCache = {};

export const resetJavascriptCache = () => (JS_ASSET_CACHE = {});
export const resetStyleCache = () => (CSS_ASSET_CACHE = {});

export const loadJavascript = (id: string, src: string, attributes: Record<string, any> = {}) =>
    loadElement('script', id, JS_ASSET_CACHE, {
        src,
        ...attributes
    });

export const loadStyle = (id: string, href: string, attributes: Record<string, any> = {}) =>
    loadElement('link', id, CSS_ASSET_CACHE, {
        type: 'text/css',
        rel: 'stylesheet',
        media: 'all',
        href,
        ...attributes
    });

const loadElement = (
    tagName: keyof HTMLElementTagNameMap,
    id,
    cache: AssetCache,
    attributes: Record<string, any> = {}
) => {
    // existing asset promise
    if (cache[id]) {
        return cache[id];
    }

    // new asset
    cache[id] = new Promise<HTMLElementTagNameMap[typeof tagName]>((resolve, reject) => {
        const element = document.createElement(tagName);
        element.id = id;

        Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
        element.onload = () => {
            resolve(element);
        };
        element.onerror = (msg, url, line, col, errorObj) =>
            reject(
                new Error(
                    `${tagName} load error for ${id}\n${msg}\n${url}, ${line}:${col}\n${JSON.stringify(errorObj)}`
                )
            );

        document.body.appendChild(element);
    });

    return cache[id];
};

// @ts-ignore
export const LoadableExternalLibrary: FC<{ id: string; src: string; onLoaded?: Function; fallback?: ReactNode }> = ({
    id,
    src,
    onLoaded,
    children,
    fallback = null
}) => {
    const [isLoaded, setLoaded] = useState(false);

    useEffect(() => {
        loadJavascript(id, src).then(() => {
            setLoaded(true);
            onLoaded && onLoaded();
        });
    }, [id, onLoaded, src]);

    if (!isLoaded) {
        return fallback;
    }

    return children;
};
